import { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "store/types";
import { getSystemUsers, inviteAdmin, resendAdminInvite, removeSystemUser } from "./actions";
import { AdminSystemUsersState, AdminSystemUsersDispatch } from "./types";

const useAdminSystemUsersState = (): AdminSystemUsersState =>
  useSelector((state: AppState) => {
    return state.admin.systemUsers;
  });

const useAdminSystemUsersDispatch = (): AdminSystemUsersDispatch => {
  const dispatch = useDispatch();
  return useMemo(
    () => ({
      getSystemUsers: () => dispatch(getSystemUsers()),
      inviteAdmin: (email) => dispatch(inviteAdmin({ email })),
      resendAdminInvite: (email) => dispatch(resendAdminInvite({ email })),
      removeSystemUser: (userId) => dispatch(removeSystemUser(userId)),
    }),
    [dispatch]
  );
};

export const useSystemUsers = (): [AdminSystemUsersState, AdminSystemUsersDispatch] => {
  return [useAdminSystemUsersState(), useAdminSystemUsersDispatch()];
};

export const useActiveSystemUsers = (): [AdminSystemUsersState, AdminSystemUsersDispatch] => {
  const [{ users: allUsers, ...state }, dispatch] = useSystemUsers();
  const users = useMemo(() => allUsers.filter((user) => user.isActive), [allUsers]);
  return [{ ...state, users }, dispatch];
};

export const useAdminSystemUsers = (): [AdminSystemUsersState, AdminSystemUsersDispatch] => {
  const [{ users: activeUsers, ...state }, dispatch] = useActiveSystemUsers();
  const users = useMemo(() => activeUsers.filter((user) => user.isAdmin), [activeUsers]);
  return [{ ...state, users }, dispatch];
};

export const useArchivedSystemUsers = (): [AdminSystemUsersState, AdminSystemUsersDispatch] => {
  const [{ users: allUsers, ...state }, dispatch] = useSystemUsers();
  const users = useMemo(() => allUsers.filter((user) => !user.isActive), [allUsers]);
  return [{ ...state, users }, dispatch];
};
